import React from 'react';
import {
  TrendingUp, Users, IndianRupee, Calendar, Star, Activity, Heart,
  BarChart3, AlertCircle
} from 'lucide-react';

const formatCurrency = (value) => `₹${Math.round(value || 0).toLocaleString('en-IN')}`;

const AdvancedAnalytics = ({ analytics = {} }) => {
  const {
    totalBookings = 0,
    totalRevenue = 0,
    totalGMV = 0,
    totalUsers = 0,
    totalCaregivers = 0,
    averageRating = 0,
    totalReviews = 0,
    completedBookings = 0,
    pendingBookings = 0,
    totalOfflineJobs = 0,
    totalOfflineRevenue = 0,
    totalCouponRedemptions = 0,
    bookingSuccessRate = 0,
  } = analytics;

  const executiveKPIs = [
    {
      title: 'Platform Revenue',
      value: formatCurrency(totalRevenue),
      icon: IndianRupee,
      subtitle: 'Commission from bookings',
    },
    {
      title: 'Total Bookings',
      value: String(totalBookings),
      icon: Calendar,
      subtitle: `${completedBookings} completed, ${pendingBookings} pending`,
    },
    {
      title: 'Registered Users',
      value: String(totalUsers),
      icon: Users,
      subtitle: 'Owners and caregivers',
    },
    {
      title: 'Avg Rating',
      value: totalReviews > 0 ? `${averageRating}★` : '—',
      icon: Star,
      subtitle: `${totalReviews} reviews`,
    },
    {
      title: 'Offline Jobs',
      value: String(totalOfflineJobs),
      icon: Activity,
      subtitle: formatCurrency(totalOfflineRevenue),
    },
    {
      title: 'Caregivers',
      value: String(totalCaregivers),
      icon: Heart,
      subtitle: 'Active on platform',
    },
  ];

  const hasActivity = totalBookings > 0 || totalUsers > 1 || totalOfflineJobs > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#0c0f14] tracking-tight">Analytics</h1>
        <p className="text-sm text-[#5c6670] mt-1">Live metrics from your platform data</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {executiveKPIs.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <div key={kpi.title} className="admin-panel p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#eaf3ef] flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-[#0c0f14]" strokeWidth={2} />
                </div>
                <span className="text-sm font-semibold text-[#5c6670]">{kpi.title}</span>
              </div>
              <div className="text-3xl font-bold text-[#0c0f14] tracking-tight mb-1">{kpi.value}</div>
              <div className="text-sm text-[#5c6670]">{kpi.subtitle}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="admin-panel p-4">
          <p className="text-sm text-[#5c6670]">Gross booking value</p>
          <p className="text-2xl font-bold text-[#0c0f14] mt-1">{formatCurrency(totalGMV)}</p>
        </div>
        <div className="admin-panel p-4">
          <p className="text-sm text-[#5c6670]">Booking success rate</p>
          <p className="text-2xl font-bold text-[#0c0f14] mt-1">{bookingSuccessRate}%</p>
        </div>
        <div className="admin-panel p-4">
          <p className="text-sm text-[#5c6670]">Coupon redemptions</p>
          <p className="text-2xl font-bold text-[#0c0f14] mt-1">{totalCouponRedemptions}</p>
        </div>
      </div>

      {!hasActivity ? (
        <div className="admin-panel text-center py-16">
          <BarChart3 className="w-12 h-12 text-[#c5d4cb] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[#0c0f14] mb-2">No activity yet</h3>
          <p className="text-[#5c6670] max-w-md mx-auto text-sm">
            Charts and trends will appear here once users sign up, caregivers join, and bookings are made.
          </p>
        </div>
      ) : (
        <div className="admin-panel p-5">
          <div className="flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-[#0c0f14] mt-0.5" />
            <div>
              <h3 className="font-semibold text-[#0c0f14] mb-1">Detailed charts coming soon</h3>
              <p className="text-sm text-[#5c6670]">
                KPI cards above reflect real Firestore data. Advanced trend charts will populate as booking volume grows.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="admin-panel-mint p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#0c0f14] mt-0.5 shrink-0" />
          <p className="text-sm text-[#5c6670]">
            All numbers on this page are computed from live platform data — no sample or demo values.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdvancedAnalytics;
